import { useMemo } from 'react'
import { MantineReactTable, MRT_ColumnDef, useMantineReactTable } from 'mantine-react-table'
import { useMantineTheme } from '@mantine/core'
import { tableDefaultProps } from '@shared/components/table/table'
import { NoDataMessage } from '@shared/components/table/table-no-data-message'
import { displayRange } from '@/helpers/typing/program/typing-program-data'
import { ContractDetail } from '@loom-api-types'
import { IconAlert, AlertLevel } from '@shared/components/icon-alert/icon-alert'

interface ContractsTableProps {
  data?: ContractDetail[]
  renderKey?: string
  noDataMessage?: string
  isLoading?: boolean
  isError?: boolean
}

export const ContractsTable = ({
  data = [],
  renderKey = '',
  noDataMessage = 'No contracts available for this wool lot',
  isLoading,
  isError = false,
}: ContractsTableProps) => {
  const theme = useMantineTheme()

  const columns = useMemo<MRT_ColumnDef<ContractDetail>[]>(
    () => [
      {
        accessorKey: 'contractName',
        id: 'contractName',
        header: 'Contract Name',
      },
      {
        accessorKey: 'outstanding',
        id: 'outstanding',
        header: 'Outstanding',
        mantineTableBodyCellProps: ({ cell }) => ({
          bg: cell.getValue() === 0 ? undefined : theme.colors.yellow[0],
        }),
      },
      {
        accessorKey: 'contracted',
        id: 'contracted',
        header: 'Contracted',
      },
      {
        accessorKey: 'targetMicronMin',
        id: 'targetMicronMin',
        header: 'Mic Range',
        accessorFn: (row) => displayRange(row.targetMicronMin, row.targetMicronMax),
      },
      {
        accessorKey: 'vmMax',
        id: 'vmMax',
        header: 'VM Max',
      },
      {
        accessorKey: 'greasyLengthRangeMin',
        id: 'greasyLengthRangeMin',
        header: 'Len Range',
        accessorFn: (row) => displayRange(row.greasyLengthRangeMin, row.greasyLengthRangeMax),
      },
      {
        accessorKey: 'nktMin',
        id: 'nktMin',
        header: 'NKT Min',
      },
    ],
    []
  )

  const table = useMantineReactTable({
    ...tableDefaultProps,
    columns,
    data,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    state: {
      showSkeletons: isLoading,
    },
    enablePagination: false,
    renderEmptyRowsFallback: () =>
      isError ? (
        <IconAlert level={AlertLevel.ERROR} title='Error loading contracts data' />
      ) : (
        <NoDataMessage noDataMessage={noDataMessage} />
      ),
    mantineTableProps: {
      ...tableDefaultProps.mantineTableProps,
      'aria-label': 'Season Contracts',
    },
  })

  return <MantineReactTable table={table} key={renderKey} />
}
